/**
 * Image compression before upload (Location Service Engine ``image_compression``): large photos are scaled to at
 * most ``maxSide`` px and re-encoded as JPEG, so uploads work on slow rural connections. GIFs, small files and
 * anything the browser can't decode are returned unchanged — compression never blocks an upload.
 */
export async function compressImage(file, { maxSide = 1600, quality = 0.8, minBytes = 300 * 1024 } = {}) {
    try {
        if (!file || !/^image\/(jpe?g|png|webp|heic|heif)$/i.test(file.type || "") || file.size < minBytes) return file;
        const bitmap = await loadBitmap(file);
        if (!bitmap) return file;
        const { width, height } = bitmap;
        const scale = Math.min(1, maxSide / Math.max(width, height));
        const w = Math.max(1, Math.round(width * scale));
        const h = Math.max(1, Math.round(height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) return file;
        ctx.drawImage(bitmap, 0, 0, w, h);
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
        if (!blob || blob.size >= file.size) return file;
        const name = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
        return new File([blob], name, { type: "image/jpeg", lastModified: Date.now() });
    } catch {
        return file;
    }
}

function loadBitmap(file) {
    if (typeof createImageBitmap === "function") {
        return createImageBitmap(file).catch(() => loadViaImg(file));
    }
    return loadViaImg(file);
}

function loadViaImg(file) {
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
        img.src = url;
    });
}
